import { useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../Button/Button'
import { cx } from '../utils/cx'
import type { SheetProps } from './types'
import './Sheet.css'

/**
 * Hoja inferior en móvil / modal centrado de 480 px en escritorio (DESIGN.md §4 y §8).
 * Confirmaciones de dinero o irreversibles: resumen y dos botones apilados, primario arriba.
 */
export function Sheet({ open, title, children, confirmLabel, cancelLabel, onConfirm, onCancel, destructive, loading, confirmHidden, inline, className }: SheetProps) {
  const { t } = useTranslation()
  const uid = useId()
  const titleId = `hp-sheet-${uid}-title`
  const panelRef = useRef<HTMLDivElement>(null)
  // onCancel suele ser una función nueva en cada render: si fuera dependencia del efecto, el foco volvería al
  // primer control con cada tecla que se escribe en un campo de la hoja.
  const cancelRef = useRef(onCancel)
  useEffect(() => {
    cancelRef.current = onCancel
  })

  useEffect(() => {
    if (!open || inline) return
    const previous = document.activeElement as HTMLElement | null
    const first = panelRef.current?.querySelector<HTMLElement>('input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])')
    first?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelRef.current()
      if (e.key === 'Tab' && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input, textarea, select, [tabindex]:not([tabindex="-1"])')
        if (focusables.length === 0) return
        const firstEl = focusables[0]
        const lastEl = focusables[focusables.length - 1]
        if (!firstEl || !lastEl) return
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault()
          lastEl.focus()
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault()
          firstEl.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      previous?.focus()
    }
  }, [open, inline])

  if (!open) return null

  const panel = (
    <div ref={panelRef} role="dialog" aria-modal={inline ? undefined : true} aria-labelledby={titleId} className={cx('hp-sheet', className)}>
      <span className="hp-sheet__handle" aria-hidden="true" />
      <h2 id={titleId} className="hp-sheet__title">
        {title}
      </h2>
      {children && <div className="hp-sheet__body">{children}</div>}
      <div className="hp-sheet__actions">
        {!confirmHidden && (
          <Button variant={destructive ? 'destructive' : 'primary'} onClick={onConfirm} loading={loading ?? false}>
            {confirmLabel ?? t('v2.ds.sheet.confirmar')}
          </Button>
        )}
        <Button variant="secondary" onClick={onCancel}>
          {cancelLabel ?? (confirmHidden ? t('v2.ds.sheet.cerrar') : t('v2.ds.sheet.cancelar'))}
        </Button>
      </div>
    </div>
  )

  if (inline) return panel
  return (
    <div className="hp-sheet-overlay" onClick={onCancel} role="presentation">
      <div className="hp-sheet-overlay__panel" onClick={(e) => e.stopPropagation()} role="presentation">
        {panel}
      </div>
    </div>
  )
}
